import { ReactNode } from 'react'
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

interface Props {
  /** Row ids in their current visual order. */
  ids: number[]
  /** Reorder mode is off — drops are ignored. Pass the same flag to every SortableTableRow. */
  disabled?: boolean
  onMove: (fromId: number, toId: number) => void
  children?: ReactNode
}

/**
 * Drag-to-reorder for a real <table>.
 *
 * DndContext and SortableContext render no DOM of their own, so the <tbody> stays a direct child
 * of <table> and the browser keeps the table layout. This is why the drag library used by the
 * priority screens (@thaddeusjiang/react-sortable-list) cannot be reused here — it wraps every
 * item in a <div>, which is invalid inside <tbody>.
 *
 * The contexts are rendered even when reordering is off: SortableTableRow calls useSortable
 * unconditionally (hooks cannot be skipped), and that hook needs a provider above it. Switching
 * the mode off disables the rows instead of removing the context.
 */
export const SortableTableBody = ({ ids, disabled = false, onMove, children }: Props) => {
  const sensors = useSensors(
    // A small distance threshold keeps a plain click on a row from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) return

    const { active, over } = event
    if (!over || active.id === over.id) return

    onMove(Number(active.id), Number(over.id))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      {/* dnd-kit 5 accepts string ids only; the domain keeps numbers, so convert at the boundary. */}
      <SortableContext items={ids.map(String)} strategy={verticalListSortingStrategy}>
        <tbody>{children}</tbody>
      </SortableContext>
    </DndContext>
  )
}
